import { useEffect, useState } from 'react';
import { DashboardSidebar } from '../components/layout/DashboardSidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { apiFetch } from '../lib/apiClient';
import { Bell, User, Globe, LogOut } from 'lucide-react';
import { toast } from '../components/ui/sonner';

export default function SettingsPage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [displayName, setDisplayName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [weeklyReminders, setWeeklyReminders] = useState(false);
  const [dailyReminders, setDailyReminders] = useState(false);
  const [dailyReminderTime, setDailyReminderTime] = useState('09:00');
  const [isSavingGeneral, setIsSavingGeneral] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchProfile = async () => {
      try {
        const response = await apiFetch('/auth/profile', { method: 'GET' });
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'Failed to load profile');
        }

        if (isMounted) {
          const profileData = data.profile || data;
          setProfile(profileData);
          setDisplayName(profileData.displayName || '');
          setTimezone(profileData.timezone || 'UTC');
          setWeeklyReminders(profileData.weeklyRemindersEnabled === 'true' || profileData.weeklyRemindersEnabled === true);
          setDailyReminders(profileData.dailyRemindersEnabled === 'true' || profileData.dailyRemindersEnabled === true);
          setDailyReminderTime(profileData.dailyReminderTime || '09:00');
        }
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchProfile();
    return () => { isMounted = false; };
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  const handleSaveGeneral = async () => {
    setIsSavingGeneral(true);
    try {
      const response = await apiFetch('/auth/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to update profile');
      }
      toast.success('Profile updated successfully');
      
      sessionStorage.removeItem('sidebar_profile');
      window.dispatchEvent(new Event('profileUpdate'));
    } catch (err) {
      toast.error(err.message || 'Could not save profile changes');
    } finally {
      setIsSavingGeneral(false);
    }
  };

  const handleSaveNotifications = async () => {
    setIsSavingNotifications(true);
    try {
      const response = await apiFetch('/auth/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          weeklyRemindersEnabled: weeklyReminders.toString(), 
          dailyRemindersEnabled: dailyReminders.toString(),
          dailyReminderTime
        })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to update notifications');
      }
      toast.success('Notification preferences updated');
    } catch (err) {
      toast.error(err.message || 'Could not save notification preferences');
    } finally {
      setIsSavingNotifications(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,97%)]">
        <DashboardSidebar />
        <main className="lg:ml-60 p-4 pt-24 lg:p-8">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="mb-8 animate-pulse">
              <div className="h-8 w-48 bg-slate-200/60 rounded mb-2"></div>
              <div className="h-4 w-64 bg-slate-200/60 rounded"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-6">
              {/* Left Column Tabs Skeleton */}
              <div className="col-span-12 lg:col-span-3">
                <div className="flex flex-col space-y-2 animate-pulse">
                  <div className="h-10 w-full bg-slate-200/60 rounded"></div>
                  <div className="h-10 w-full bg-slate-200/60 rounded"></div>
                </div>
              </div>

              {/* Right Column Content Skeleton */}
              <div className="col-span-12 lg:col-span-9 space-y-6 animate-pulse">
                {/* Profile Info Skeleton */}
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 space-y-4">
                  <div className="space-y-2 mb-6">
                    <div className="h-6 w-40 bg-slate-200/60 rounded"></div>
                    <div className="h-4 w-64 bg-slate-200/60 rounded"></div>
                  </div>
                  
                  <div className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <div className="h-4 w-24 bg-slate-200/60 rounded"></div>
                      <div className="h-10 max-w-md bg-slate-200/60 rounded"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-4 w-24 bg-slate-200/60 rounded"></div>
                      <div className="h-10 max-w-md bg-slate-200/60 rounded"></div>
                      <div className="h-3 w-48 bg-slate-200/60 rounded"></div>
                    </div>
                    <div className="h-10 w-32 bg-slate-200/60 rounded mt-4"></div>
                  </div>
                </div>

                {/* Regional Settings Skeleton */}
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 space-y-4">
                  <div className="h-6 w-40 bg-slate-200/60 rounded mb-6"></div>
                  <div className="space-y-2">
                    <div className="h-4 w-24 bg-slate-200/60 rounded"></div>
                    <div className="h-10 max-w-md bg-slate-200/60 rounded"></div>
                    <div className="h-3 w-48 bg-slate-200/60 rounded"></div>
                  </div>
                </div>

                {/* Danger Zone Skeleton */}
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 space-y-4">
                  <div className="space-y-2 mb-6">
                    <div className="h-6 w-32 bg-slate-200/60 rounded"></div>
                    <div className="h-4 w-64 bg-slate-200/60 rounded"></div>
                  </div>
                  <div className="h-20 bg-slate-200/40 rounded-xl border border-slate-100"></div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,97%)]">
        <DashboardSidebar />
        <main className="lg:ml-60 p-4 pt-24 lg:p-8 flex items-center justify-center">
          <p className="text-red-500">{error || 'Failed to load settings.'}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,97%)]">
      <DashboardSidebar />
      <main className="lg:ml-60 p-4 pt-24 lg:p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[hsl(222,47%,11%)] font-['Outfit'] mb-1">
              Account Settings
            </h1>
            <p className="text-slate-600 text-sm">
              Manage your preferences, account details, and security.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-6">
            <div className="col-span-12 lg:col-span-3">
              <nav className="flex flex-row lg:flex-col space-x-2 lg:space-x-0 space-y-0 lg:space-y-1 overflow-x-auto pb-2 hide-scroll">
                <Button 
                  variant="ghost" 
                  onClick={() => setActiveTab('general')}
                  className={`justify-start font-medium ${activeTab === 'general' ? 'text-[hsl(234,89%,63%)] bg-[hsl(234,89%,63%)]/10' : 'text-slate-600 hover:text-[hsl(222,47%,11%)] hover:bg-slate-100'}`}
                >
                  <User className="w-4 h-4 mr-2" />
                  General
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => setActiveTab('notifications')}
                  className={`justify-start font-medium ${activeTab === 'notifications' ? 'text-[hsl(234,89%,63%)] bg-[hsl(234,89%,63%)]/10' : 'text-slate-600 hover:text-[hsl(222,47%,11%)] hover:bg-slate-100'}`}
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notifications
                </Button>
              </nav>
            </div>

            <div className="col-span-12 lg:col-span-9 space-y-6">
              {activeTab === 'general' && (
                <>
                  <Card className="card-elevated border-0">
                    <CardHeader>
                      <CardTitle className="text-lg font-['Outfit'] text-[hsl(222,47%,11%)]">Profile Information</CardTitle>
                      <CardDescription>Update your account's profile information and email address.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="displayName" className="text-slate-700">Display Name</Label>
                        <Input 
                          id="displayName" 
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          className="max-w-md" 
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                        <Input id="email" type="email" defaultValue={profile.email || ''} className="max-w-md" disabled />
                        <p className="text-xs text-slate-500">Your email address is managed by your sign-in provider.</p>
                      </div>
                      <Button 
                        onClick={handleSaveGeneral} 
                        disabled={isSavingGeneral}
                        className="bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white mt-2"
                      >
                        {isSavingGeneral ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="card-elevated border-0">
                    <CardHeader>
                      <CardTitle className="text-lg font-['Outfit'] text-[hsl(222,47%,11%)]">Regional Settings</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-slate-700 flex items-center gap-2">
                          <Globe className="w-4 h-4 text-slate-500" />
                          Timezone
                        </Label>
                        <Input 
                          value={timezone} 
                          disabled
                          className="max-w-md" 
                        />
                        <p className="text-xs text-slate-500">Your time zone is automatically determined.</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-elevated border-0 border-red-100">
                    <CardHeader>
                      <CardTitle className="text-lg font-['Outfit'] text-red-600">Danger Zone</CardTitle>
                      <CardDescription>Irreversible actions related to your account.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between p-4 border border-red-100 bg-red-50/50 rounded-xl">
                        <div>
                          <h4 className="font-medium text-red-900 text-sm">Sign Out</h4>
                          <p className="text-xs text-red-700 mt-0.5">Log out of your current session.</p>
                        </div>
                        <Button variant="destructive" onClick={() => setShowSignOutModal(true)} className="bg-red-600 hover:bg-red-700 text-sm h-9">
                          <LogOut className="w-4 h-4 mr-2" />
                          Sign Out
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              {activeTab === 'notifications' && (
                <Card className="card-elevated border-0">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Outfit'] text-[hsl(222,47%,11%)]">Notification Preferences</CardTitle>
                    <CardDescription>Manage how we contact you through email.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between space-x-2">
                      <div className="space-y-0.5">
                        <Label htmlFor="weekly-reminders" className="text-base text-slate-700">Weekly Reminders</Label>
                        <p className="text-sm text-slate-500 text-muted-foreground">
                          Receive emails about your progress and suggested exercises to keep up your streak.
                        </p>
                      </div>
                      <Switch 
                        id="weekly-reminders" 
                        checked={weeklyReminders}
                        onCheckedChange={setWeeklyReminders}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between space-x-2">
                      <div className="space-y-0.5">
                        <Label htmlFor="daily-reminders" className="text-base text-slate-700">Daily Reminders</Label>
                        <p className="text-sm text-slate-500 text-muted-foreground">
                          Receive a daily nudge to remind you to practice and maintain your streak.
                        </p>
                      </div>
                      <Switch 
                        id="daily-reminders" 
                        checked={dailyReminders}
                        onCheckedChange={setDailyReminders}
                      />
                    </div>

                    {dailyReminders && (
                      <div className="space-y-2 pt-2 border-t border-slate-100">
                        <Label htmlFor="reminder-time" className="text-slate-700">Daily Reminder Time</Label>
                        <Input 
                          id="reminder-time" 
                          type="time" 
                          value={dailyReminderTime}
                          onChange={(e) => setDailyReminderTime(e.target.value)}
                          className="max-w-[200px]" 
                        />
                        <p className="text-xs text-slate-500">Pick the time you prefer to be notified (in your local timezone).</p>
                      </div>
                    )}

                    <Button 
                      onClick={handleSaveNotifications} 
                      disabled={isSavingNotifications}
                      className="bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white mt-2"
                    >
                      {isSavingNotifications ? 'Saving...' : 'Save Preferences'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Sign Out Confirmation Modal */}
      <Dialog open={showSignOutModal} onOpenChange={setShowSignOutModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-['Outfit'] text-[hsl(222,47%,11%)]">
              <LogOut className="w-5 h-5 text-red-500" />
              Sign Out
            </DialogTitle>
            <DialogDescription className="text-slate-600 mt-2">
              Are you sure you want to sign out of your account? You will need to log back in to access your dashboard and roadmap.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-4 sm:justify-end gap-2 flex-row">
            <Button
              variant="outline"
              onClick={() => setShowSignOutModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleSignOut}
            >
              Sign Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
